/**
 * AOI 生产管理平台 V2 - 共享类型。
 */

import type { SourceGroup } from './constants';

/** 底图三选一 (Leaflet 图层): OSM 瓦片 / Esri World Imagery 卫星 / Google 路网(GoogleMutant)。 */
export type BasemapType = 'osm' | 'google' | 'satellite';

/** 地图交互模式: 空闲 / 绘制新面 / 编辑选中面形状。 */
export type DrawMode = 'idle' | 'draw' | 'edit';

/** 抽屉里"地图选点"的目标 (进门点 / 停车点); null = 不在选点态。 */
export type PickTarget = 'entry' | 'parking' | null;

/** GeoJSON 几何 (只用到 Polygon / MultiPolygon)。 */
export interface GeoJsonGeometry {
    type: string;
    coordinates: unknown;
}

/** 视口 bbox + zoom (zoom 统一按 Google 语义)。 */
export interface Viewport {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
    zoom: number;
}

/** 当前选中的 AOI (zipcode 是后端所有详情 / 变更接口的必带参数)。 */
export interface SelectedAoi {
    aoiId: number;
    zipcode: string;
    /** 从列表点击选中时带过来的创建时间, 在 detail (aoiCreatedAt) 回来前先顶着; 地图点击选中时为 null。 */
    createdAt?: string | null;
}

/** 页面下发给地图的一次性视图命令 (nonce 变化才执行)。 */
export type ViewCommand =
    | {
        kind: 'fit';
        minLng: number; minLat: number; maxLng: number; maxLat: number;
        maxZoom?: number;
        /**
         * fit 之后的 zoom 下限。面很大时 fitBounds 会缩到很小的 zoom, 低于后端 MIN_RENDER_ZOOM
         * 就一个 AOI 都不返回 —— 表现是"点了列表, 只剩选中那个面, 周边一片空白, 点建筑也没反应"。
         */
        minZoom?: number;
        nonce: number;
    }
    | { kind: 'set'; lat: number; lng: number; zoom: number; nonce: number };

/**
 * 分发式 Omit: 原生 Omit 作用于联合类型时不分发, 会把各分支的判别属性 (kind) 一起丢掉,
 * 导致传 { kind: 'fit', ... } 字面量赋值失败。ViewCommandInput 用于"调用方不填 nonce"的入参。
 */
type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

/** 发起视野指令的入参 (nonce 由 hook 内部自增填充)。 */
export type ViewCommandInput = DistributiveOmit<ViewCommand, 'nonce'>;

/** 地图上一次点选出的坐标 (nonce 用于同一位置连点两次也能触发 effect)。 */
export interface PickedPoint {
    lng: number;
    lat: number;
    nonce: number;
}

/** 抽屉里维护、地图上要显示的点位 (进门 / 停车)。 */
export interface DrawerPoint {
    lng: number;
    lat: number;
    remark?: string | null;
}

/**
 * 绘制进行中 (leaflet-draw handler 还没闭合) 可用的命令式操作。
 *
 * <p>handler 活在 MapCanvas 内部, 页面拿不到; 通过 ref 把动作暴露出来, 让工具栏能提供
 * "撤销上一个点 / 直接闭合" —— 否则点错了只能整个退出重画。
 */
export interface DrawActions {
    /** 撤销刚点下的那个顶点。 */
    deleteLastVertex: () => void;
    /** 用已点的顶点直接闭合成面 (等价于双击)。 */
    completeShape: () => void;
}

/**
 * 抽屉里编辑中的门禁码草稿 (AOI 级门禁, 跟 biz_aoi_attribute.access_codes 一一对应)。
 *
 * <p>id 是 biz_access_code 行主键: 查询带回、保存原样回传, 服务端按它精确识别"改的是哪一行"
 * (原地改, 不换行 —— 反馈/审计的锚点不断), 新增的码填 null。uid 仍不回传(服务端按内容重算)。
 */
export interface AccessCodeDraft {
    /** 行 id(查询带回, 保存原样回传); 新增的码为 null */
    id?: string | null;
    codeType: string;
    codeValue: string;
    /** PENDING_VERIFICATION / AVAILABLE / UNAVAILABLE */
    available: string;
    remark?: string | null;
}

/**
 * 抽屉里编辑中的业务标签草稿 (biz_aoi_attribute.biz_tags 一项)。
 *
 * <p>只保留运营能改的字段: code + 备注 (+ 预留的参数 JSON, 本期 UI 不开放)。
 * uid / kind / coverZipcodes / version 都由服务端按围栏重算, 保存时不回传。
 */
export interface BizTagDraft {
    tag: string;
    remark?: string | null;
    paramsJson?: string | null;
}

/** 来源图层开关。 */
export type SourceVisibility = Record<SourceGroup, boolean>;

/**
 * 生效状态筛选 (与 BFF 枚举 AoiEffectiveStatusFilter 一一对应)。
 *
 * <p>ALL 是 PRD 定的默认值 (含未生效 / 待审核的面); 单值 (UNVERIFIED / VERIFIED) 是审核视图:
 * 后端按 aoiId 升序返回, 前端单页收到 AUDIT_LIST_PAGE_SIZE 条。不传给后端 = 只看已生效 (旧行为), 本页不用。
 */
export type EffectiveStatusFilter = 'ALL' | 'VERIFIED' | 'UNVERIFIED';

export const EFFECTIVE_STATUS_FILTER_OPTIONS: { value: EffectiveStatusFilter; label: string }[] = [
    { value: 'ALL', label: '全部' },
    { value: 'UNVERIFIED', label: '未生效' },
    { value: 'VERIFIED', label: '已生效' },
];

/** 单值生效状态筛选 = 审核视图 (决定 50 条上限与 aoiId 升序); 「全部」维持既有列表行为。 */
export function isAuditStatusFilter(filter: EffectiveStatusFilter): boolean {
    return filter !== 'ALL';
}

/**
 * 级别筛选 (2026-09-18): 审算法 2 级面时把一个 zipcode 里几千栋 1 级楼过滤掉。
 * 后端契约 (map-api / api-server) 的 level 字段早已存在, 只是前端一直没传; 取值走 DB 口径 0 = 1 级(楼栋) / 1 = 2 级(小区)。
 */
export type LevelFilter = 'ALL' | 'LEVEL_1' | 'LEVEL_2';

export const LEVEL_FILTER_OPTIONS: { value: LevelFilter; label: string }[] = [
    { value: 'ALL', label: '全部' },
    { value: 'LEVEL_1', label: '1 级' },
    { value: 'LEVEL_2', label: '2 级' },
];

/** 级别筛选 → 接口 level (DB 口径): ALL → null (不过滤), LEVEL_1 → 0, LEVEL_2 → 1。映射只在这一处, JSX 里不散写。 */
export function levelFilterToNumeric(filter: LevelFilter): number | null {
    if (filter === 'LEVEL_1') return 0;
    if (filter === 'LEVEL_2') return 1;
    return null;
}

/**
 * 抽屉「生效状态」下拉取值: 直接用 biz_aoi.status 的 DB 值 (1=已生效 / 0=未生效), 不再做一层映射。
 * 存量 1 级 AOI 默认已生效 (DB DEFAULT 1)。
 */
export const EFFECTIVE_STATUS_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: '已生效' },
    { value: 0, label: '未生效' },
];

/**
 * 后端拿不到精确总数时返回的哨兵值。
 *
 * <p>不填 Zipcode 的跨分区查询不发 COUNT —— 精确 COUNT 加不了 LIMIT, 而有界 COUNT 会被
 * PolarDB 优化器判成大查询走并行, 实测恒定 13.6 秒。后端改成多取一条判断有没有下一页:
 * 最后一页能给出准确总数, 还有下一页时返回 -1, 前端显示成"更多"。
 */
export const TOTAL_UNKNOWN = -1;

/** 列表总数展示: 哨兵值转成"更多"。 */
export function formatListTotal(total: number): string {
    return total === TOTAL_UNKNOWN ? '更多' : String(total);
}

/** biz_aoi.status → 中文; null 按已生效兜底 (老数据 / 老接口不带该字段时不要显示成"未生效")。 */
export function effectiveStatusLabel(status: number | null | undefined): string {
    return status === 0 ? '未生效' : '已生效';
}

/** 筛选区状态。 */
export interface FiltersState {
    id: string;
    name: string;
    lat: string;
    lng: string;
    zipcode: string;
    /** 关联地址模糊关键词 (命中任一条关联地址即算匹配)。 */
    address: string;
    /** 毫秒时间戳; null = 未选。 */
    createdFrom: number | null;
    createdTo: number | null;
    /** 生效状态 (人工审核视图); 默认 ALL。 */
    effectiveStatus: EffectiveStatusFilter;
    /** 级别 (1 级楼栋 / 2 级小区); 默认 ALL。 */
    level: LevelFilter;
    /** 业务标签 code 精确筛 (通用标签筛选, 不是某个场景专属); null = 不筛。需要 Zipcode。 */
    tagCode: string | null;
}

/** 空筛选。 */
export const EMPTY_FILTERS: FiltersState = {
    id: '',
    name: '',
    lat: '',
    lng: '',
    zipcode: '',
    address: '',
    createdFrom: null,
    createdTo: null,
    effectiveStatus: 'ALL',
    level: 'ALL',
    tagCode: null,
};

/** 数字 level (detail / list 接口) → 枚举 (bbox / create 接口): 0 = LEVEL_1(楼栋), 1 = LEVEL_2(小区)。 */
export function numericLevelToEnum(level: number | null | undefined): string | null {
    if (level == null) return null;
    return level === 0 ? 'LEVEL_1' : 'LEVEL_2';
}

/** LEVEL_1 → "1级", LEVEL_2 → "2级"。 */
export function levelEnumLabel(levelEnum: string | null | undefined): string {
    if (levelEnum === 'LEVEL_1') return '1级';
    if (levelEnum === 'LEVEL_2') return '2级';
    return '-';
}
